/**
 * lib/timetable.ts
 * ------------------------------------------------------------------
 * Reading the timetable inside the portal.
 *
 * The timetable is AUTHORED in the standalone alnur-timetable-v2 app, which
 * now writes into this same Firestore project (see TIMETABLE-INTEGRATION-PLAN.md).
 * The portal never writes any of it — everything here reads.
 *
 * THE TWO-DOCUMENT MODEL
 *   `timetableSlots`    one document per lesson, covering a period RANGE and
 *                       possibly several classes at once.
 *   `timetablePointers` one document per (class, day, period), keyed
 *                       "Form 1A-Monday-1", holding the slot id.
 *
 * A double lesson therefore has ONE slot and TWO pointers. The grid renders
 * from pointers; `period_start === period` marks the first cell so a caller can
 * span the rest instead of repeating the lesson.
 *
 * CLASSES ARE NAMES, NOT IDS
 *   Pointer keys carry the class NAME ("Form 1A") because the authoring app
 *   writes names. Portal class ids ("Form1A") are resolved through
 *   `reportClasses.name`, never stored alongside — a denormalised copy would
 *   go stale the moment a slot is edited.
 * ------------------------------------------------------------------ */

/* LKC RUNS A 6-DAY CYCLE, not Monday–Friday (TIMETABLE-PLAN.md, 31 Oct 2026).
   A timetable "day" is a cycle day, and pointer keys read "Form 2 S-Day 3-4".
   Which cycle day a DATE is comes from School Settings → Teaching Days
   (lib/teachingCycle.ts): the cycle rolls on through teaching days and pauses
   on weekends, holidays, breaks and closures. Code that starts from a date
   asks dayForDate(); nothing here may assume a weekday. */
export type Day = 'Day 1' | 'Day 2' | 'Day 3' | 'Day 4' | 'Day 5' | 'Day 6';
export const DAYS: Day[] = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'];

/* The date → cycle-day lookup, registered by useTeachingCycle once the school's
   settings, holidays and term dates are loaded. Until then no date is a
   teaching day, which every caller already handles (the weekend case). */
let cycleOf: (iso: string) => number | null = () => null;
export function setCycleResolver(fn: (iso: string) => number | null): void { cycleOf = fn; }

/** The timetable day a date is, or null when it has no teaching. */
export function dayForDate(iso: string): Day | null {
  const n = cycleOf(iso);
  return n && n >= 1 && n <= DAYS.length ? DAYS[n - 1] : null;
}

/** The next date after `iso` that has teaching, and its cycle day (within a month). */
export function nextTeachingDay(iso: string): { date: string; day: Day } | null {
  const [y, m, d] = iso.split('-').map(Number);
  for (let i = 1; i <= 31; i++) {
    const date = new Date(Date.UTC(y, m - 1, d + i)).toISOString().slice(0, 10);
    const day = dayForDate(date);
    if (day) return { date, day };
  }
  return null;
}

export type Period = number;
export type SlotId = string;
export type PointerKey = string;

/** A period slot in a day's schedule. Non-teaching slots use string labels so
 *  they sit in the grid without being mistaken for teaching periods. */
export type PeriodLabel = number | 'Registration' | 'BREAK';

export interface PeriodSlot {
  period: PeriodLabel;
  time: string; // "07:15-07:45"
}

export interface ScheduleSettings {
  division?: string;
  days: Record<Day, PeriodSlot[]>;
}

export function isTeachingSlot(s: PeriodSlot): s is PeriodSlot & { period: number } {
  return typeof s.period === 'number';
}

/**
 * What a lesson group IS. Set during migration and carried on every group.
 *
 *   teaching     a normal examined lesson; has a portal subjectId
 *   enrichment   taught but not examined (Robotics) — real teaching, no subject
 *   supervision  a free teacher minding pupils (GC) — NOT teaching
 *
 * The last two both lack a subjectId but are not interchangeable: only
 * `enrichment` counts toward a teaching load. GC is allocated by looking at
 * teaching load, so counting it would corrupt the measure that drives it.
 */
export type SlotKind = 'teaching' | 'enrichment' | 'supervision';

/** A teacher referenced by the timetable who has no portal record yet —
 *  Islamic-section staff pending onboarding. Carried so the lesson still
 *  renders with everyone who teaches it. */
export interface UnresolvedTeacher {
  tchId: string;
  code: string | null;
  name: string | null;
}

/** One sub-lesson within a combined-split slot (an option block). */
export interface SlotGroup {
  classes?: string[];
  teacherIds: string[];
  unresolvedTeachers?: UnresolvedTeacher[];
  room: string;
  subjectLabel?: string;
  subjectId?: string | null;
  streamLabel?: string | null;
  kind?: SlotKind;
  subjectUnmapped?: boolean;
}

export interface TimetableSlot {
  id: SlotId;
  scope: 'class' | 'class-coteach' | 'combined-uniform' | 'combined-split';
  day: Day;
  period_start: Period;
  period_end: Period;
  classes: string[];
  /** Present on every scope except combined-split. */
  teacherIds?: string[];
  unresolvedTeachers?: UnresolvedTeacher[];
  room?: string;
  subjectLabel?: string;
  /**
   * What the AUTHORING APP writes. The two front ends name this field
   * differently — the app renders `subject`, the portal renders
   * `subjectLabel` — so a slot created in the app carries only this one and
   * reads as blank here unless we fall back to it. See slotGroups().
   */
  subject?: string;
  subjectId?: string | null;
  streamLabel?: string | null;
  kind?: SlotKind;
  subjectUnmapped?: boolean;
  /** Present only on combined-split. */
  groups?: SlotGroup[];
}

export interface TimetablePointer {
  slotId: SlotId;
}

/* ==================== pointer keys ==================== */

export function makePointerKey(cls: string, day: Day, period: Period): PointerKey {
  return `${cls}-${day}-${period}`;
}

/**
 * Split "Form 1A-Monday-1" back into its parts.
 *
 * Parsed from the RIGHT, because a class name may itself contain a hyphen.
 * Returns null on anything malformed rather than throwing — a stray document
 * should not blank a teacher's whole timetable.
 */
export function parsePointerKey(
  key: PointerKey,
): { cls: string; day: Day; period: Period } | null {
  const lastDash = key.lastIndexOf('-');
  if (lastDash < 0) return null;
  const period = Number(key.slice(lastDash + 1));
  if (!Number.isFinite(period)) return null;
  const rest = key.slice(0, lastDash);
  const secondLast = rest.lastIndexOf('-');
  if (secondLast < 0) return null;
  const day = rest.slice(secondLast + 1) as Day;
  if (!DAYS.includes(day)) return null;
  return { cls: rest.slice(0, secondLast), day, period };
}

/** The Firestore documentId() range that selects one class's pointers.
 *  Lets a parent fetch their child's class alone instead of all 832. */
export function pointerRangeForClass(cls: string): [string, string] {
  return [`${cls}-`, `${cls}-`];
}

/* ==================== slot helpers ==================== */

/** Every teacher id in this slot, top-level or nested in groups. */
export function slotTeacherIds(slot: TimetableSlot): string[] {
  const out = new Set<string>();
  (slot.teacherIds ?? []).forEach((t) => out.add(t));
  (slot.groups ?? []).forEach((g) => (g.teacherIds ?? []).forEach((t) => out.add(t)));
  return [...out];
}

/** Unresolved teachers anywhere in this slot. */
export function slotUnresolvedTeachers(slot: TimetableSlot): UnresolvedTeacher[] {
  const out: UnresolvedTeacher[] = [];
  (slot.unresolvedTeachers ?? []).forEach((u) => out.push(u));
  (slot.groups ?? []).forEach((g) => (g.unresolvedTeachers ?? []).forEach((u) => out.push(u)));
  return out;
}

/** Every room this slot uses. */
export function slotRooms(slot: TimetableSlot): string[] {
  const out = new Set<string>();
  if (slot.room) out.add(slot.room);
  (slot.groups ?? []).forEach((g) => { if (g.room) out.add(g.room); });
  return [...out];
}

/** The groups of a split slot, or the slot itself presented as one group. */
export function slotGroups(slot: TimetableSlot): SlotGroup[] {
  if (slot.groups && slot.groups.length) return slot.groups;
  return [{
    teacherIds: slot.teacherIds ?? [],
    unresolvedTeachers: slot.unresolvedTeachers,
    room: slot.room ?? '',
    // `subject` is the authoring app's name for this field. Without the
    // fallback every lesson a class teacher takes themselves — the whole
    // academic half of the primary week — drew an empty tile.
    subjectLabel: slot.subjectLabel ?? slot.subject,
    subjectId: slot.subjectId,
    streamLabel: slot.streamLabel,
    kind: slot.kind,
    subjectUnmapped: slot.subjectUnmapped,
  }];
}

/** How many periods this slot occupies. */
export function slotLength(slot: TimetableSlot): number {
  return Math.max(1, slot.period_end - slot.period_start + 1);
}

/* ==================== grids ==================== */

export type Grid = Record<Day, Record<Period, TimetableSlot | null>>;

const emptyGrid = (): Grid =>
  DAYS.reduce((acc, d) => { acc[d] = {}; return acc; }, {} as Grid);

/** Grid for one class: day -> period -> slot. */
export function classGrid(
  cls: string,
  pointers: Record<PointerKey, TimetablePointer>,
  slots: Record<SlotId, TimetableSlot>,
): Grid {
  const out = emptyGrid();
  for (const [key, ptr] of Object.entries(pointers)) {
    const parsed = parsePointerKey(key);
    if (!parsed || parsed.cls !== cls) continue;
    const s = slots[ptr.slotId];
    if (s) out[parsed.day][parsed.period] = s;
  }
  return out;
}

/**
 * Grid for one teacher, built from the SLOTS rather than the pointers.
 *
 * A teacher's lessons are not reachable through pointers without scanning
 * every class, and a slot already carries its own day and period range — so
 * this reads the slots directly and fills each period the lesson covers.
 */
export function teacherGrid(
  teacherId: string,
  slots: Record<SlotId, TimetableSlot>,
): Grid {
  const out = emptyGrid();
  for (const s of Object.values(slots)) {
    if (!slotTeacherIds(s).includes(teacherId)) continue;
    for (let p = s.period_start; p <= s.period_end; p++) out[s.day][p] = s;
  }
  return out;
}

/** Grid for one room. */
export function roomGrid(
  room: string,
  slots: Record<SlotId, TimetableSlot>,
): Grid {
  const out = emptyGrid();
  for (const s of Object.values(slots)) {
    if (!slotRooms(s).includes(room)) continue;
    for (let p = s.period_start; p <= s.period_end; p++) out[s.day][p] = s;
  }
  return out;
}

/**
 * Narrow a split slot to the groups a given PUPIL actually attends.
 *
 * An option block puts several lessons in one period; a pupil sits in one of
 * them. Membership comes from the pupil's own subject elections, matched on
 * `subjectId` — which is exactly why the migration resolved every label to a
 * portal subject id.
 *
 * Returns ALL groups when nothing matches, because showing the whole block is
 * honest about the uncertainty; silently showing an empty period would read as
 * "you have a free" and be wrong.
 */
export function groupsForPupil(
  slot: TimetableSlot,
  pupilSubjectIds: string[],
): { groups: SlotGroup[]; narrowed: boolean } {
  const groups = slotGroups(slot);
  if (groups.length <= 1) return { groups, narrowed: false };
  const mine = groups.filter((g) => g.subjectId && pupilSubjectIds.includes(g.subjectId));
  return mine.length ? { groups: mine, narrowed: true } : { groups, narrowed: false };
}

/**
 * What the timetable knows about ONE pupil, to personalise their grid.
 *
 * `streams` maps a subjectId to the set the school has put them in — "L" or
 * "M" for Physics. A subject absent from it means nobody has said yet.
 */
export interface PupilContext {
  subjectIds: string[];
  streams: Record<string, string>;
}

/**
 * Narrow a slot to the lessons ONE pupil actually attends.
 *
 * `groupsForPupil` can only filter by subject, and Physics L and Physics M are
 * the SAME subject — the letter is the set. So a pupil taking Physics met both
 * sets on their grid, every time, with nothing to say which was theirs.
 *
 * This adds the set, and the rule for when it is not known is the point:
 *
 *   set recorded  -> only that set
 *   no set yet    -> EVERY set of that subject, flagged `unconfirmed`
 *
 * Showing both until a teacher assigns them is deliberate. Guessing would put
 * a pupil in the wrong room with confidence, and hiding both would show a free
 * period that is not free. Two lessons with a "not confirmed" note is the one
 * answer that is never wrong.
 *
 * Nothing known about the pupil falls back to every group, as before — an
 * unexpected shape should show too much, never too little.
 */
/**
 * Does a timetable subject id name one of the pupil's elected subjects?
 *
 * Not simple equality, because the two lists were keyed separately and have
 * drifted: the timetable says `business` where elections say
 * `business_studies`, and `physics_` carries a trailing underscore. An exact
 * match would treat a Business student as not taking Business.
 *
 * Tolerated: trailing underscores, and one id being the other plus a
 * `_word` suffix. NOT tolerated: two different second words —
 * `english_language` and `english_literature` are different subjects, and a
 * rule loose enough to join them would put a pupil in the wrong room.
 */
export function subjectMatches(timetableId: string, electedIds: string[]): boolean {
  const norm = (s: string) => s.replace(/_+$/, '').toLowerCase();
  const t = norm(timetableId);
  return electedIds.some((e) => {
    const x = norm(e);
    return x === t || x.startsWith(t + '_') || t.startsWith(x + '_');
  });
}

export function groupsForStudent(
  slot: TimetableSlot,
  pupil: PupilContext,
): { groups: SlotGroup[]; narrowed: boolean; unconfirmed: boolean } {
  const groups = slotGroups(slot);
  if (groups.length <= 1) return { groups, narrowed: false, unconfirmed: false };

  const mine = groups.filter((g) => g.subjectId && subjectMatches(g.subjectId, pupil.subjectIds));
  if (!mine.length) return { groups, narrowed: false, unconfirmed: false };

  let unconfirmed = false;
  const kept = mine.filter((g) => {
    const stream = g.streamLabel ?? '';
    if (!stream) return true;                       // an unsplit subject: always theirs
    const assigned = pupil.streams[g.subjectId as string];
    if (!assigned) {
      // Only "unconfirmed" when there really is a choice left to make.
      if (mine.some((o) => o !== g && o.subjectId === g.subjectId && (o.streamLabel ?? '') !== stream)) {
        unconfirmed = true;
      }
      return true;
    }
    return assigned === stream;
  });

  // A recorded set that matches nothing on this slot must not blank the lesson.
  return kept.length
    ? { groups: kept, narrowed: true, unconfirmed }
    : { groups: mine, narrowed: true, unconfirmed: false };
}

/* ==================== teachers: phase and order ==================== */

/** 'islamic' is the Islamic Department (madrasah) staff: a teacher whose ONLY
 *  phase is islamic is in neither the primary nor the secondary list, and an
 *  exam is not theirs to invigilate. */
export type TeacherPhase = 'primary' | 'secondary' | 'islamic';

/** A secondary class as the timetable names them. Everything else is primary. */
export function isSecondaryClassName(name: string): boolean {
  return /^Form\s*\d/i.test(name) || /^(AS|A2)\b/i.test(name.trim());
}

/**
 * Whether a class's pupils may have student-portal logins. The portal is for
 * SECONDARY only for now (the school, 2026-10-05), so the Student Onboarding
 * screens list no primary class. A class's recorded `division` wins; the
 * existing secondary classes are untagged, so an untagged class is judged by
 * its name.
 */
export function isStudentPortalClass(c: { name: string; division?: string }): boolean {
  if (c.division) return c.division === 'secondary';
  return isSecondaryClassName(c.name);
}

/**
 * Which phase(s) a teacher belongs to: primary, secondary, or both.
 *
 * TWO sources, UNIONED, because each misses something the other catches. HR's
 * `divisions` is the school's own statement, but it was keyed by hand and a
 * teacher can be timetabled somewhere HR never recorded. The timetable is what
 * actually happens, but today it holds only secondary classes, so on its own it
 * would file every primary teacher as having no phase at all. Together, a
 * teacher who teaches in both parts of the school appears in both lists.
 *
 * The three primary divisions (pre-primary, junior and upper primary) all count
 * as primary.
 */
export function teacherPhases(
  divisions: string[] | undefined,
  classesTaught: Iterable<string>,
): Set<TeacherPhase> {
  const out = new Set<TeacherPhase>();
  for (const d of divisions ?? []) {
    if (d === 'secondary') out.add('secondary');
    else if (d === 'islamic') out.add('islamic');
    else if (/primary/i.test(d)) out.add('primary');
  }
  for (const c of classesTaught) out.add(isSecondaryClassName(c) ? 'secondary' : 'primary');
  return out;
}

/**
 * Every class each teacher appears against, from the slots.
 *
 * Takes the map OR the array: MyTimetable holds slots keyed by id, the exam
 * rota holds them as a list, and `Object.values` reads both.
 */
export function classesByTeacher(
  slots: Record<string, TimetableSlot> | TimetableSlot[],
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const slot of Object.values(slots)) {
    for (const g of slotGroups(slot)) {
      const classes = g.classes && g.classes.length ? g.classes : (slot.classes ?? []);
      for (const t of g.teacherIds ?? []) {
        if (!out.has(t)) out.set(t, new Set());
        const set = out.get(t)!;
        classes.forEach((c) => set.add(c));
      }
    }
  }
  return out;
}

/**
 * A teacher's surname, for sorting.
 *
 * The display name is "Mrs. V. Bunjun" or "Apa Nadia", so sorting on it orders
 * the list by TITLE — every Mr before every Mrs before every Ms — which is why
 * the lists looked unordered. HR records `lastName` on every teacher; the last
 * word of the display name is the fallback for anyone added without one.
 */
export function surnameOf(t: { lastName?: string; displayName?: string; fullName?: string }): string {
  const last = (t.lastName ?? '').trim();
  if (last) return last;
  const words = (t.displayName || t.fullName || '').trim().split(/\s+/);
  return words[words.length - 1] ?? '';
}

export function compareBySurname(
  a: { lastName?: string; displayName?: string; fullName?: string },
  b: { lastName?: string; displayName?: string; fullName?: string },
): number {
  return surnameOf(a).localeCompare(surnameOf(b), undefined, { sensitivity: 'base' })
    || (a.displayName || a.fullName || '').localeCompare(b.displayName || b.fullName || '');
}

/**
 * Narrow a slot to the groups a given TEACHER actually takes.
 *
 * An option block runs several lessons at once with different teachers. On a
 * teacher's own timetable only their group belongs — showing the whole block
 * lists colleagues as though the teacher were in the room with them.
 *
 * Falls back to every group if none matches, so an unexpected shape shows too
 * much rather than showing a free period that isn't free.
 */
export function groupsForTeacher(
  slot: TimetableSlot,
  teacherId: string,
): { groups: SlotGroup[]; narrowed: boolean } {
  const groups = slotGroups(slot);
  const mine = groups.filter((g) => (g.teacherIds ?? []).includes(teacherId));
  return mine.length ? { groups: mine, narrowed: mine.length < groups.length } : { groups, narrowed: false };
}

/** Everyone else teaching alongside this teacher in these groups. */
export function coTeachers(groups: SlotGroup[], teacherId: string): string[] {
  const out = new Set<string>();
  groups.forEach((g) => (g.teacherIds ?? []).forEach((t) => { if (t !== teacherId) out.add(t); }));
  return [...out];
}

/* ==================== loads ==================== */

export interface TeacherLoad {
  teacherId: string;
  /** Periods of examined + enrichment lessons — actual teaching. */
  teachingPeriods: number;
  /** Periods of supervision (GC). Kept SEPARATE: supervision is handed to
   *  teachers with a light teaching load, so folding it in would mask the
   *  very thing the allocation looks at. */
  supervisionPeriods: number;
  /** Distinct classes taught. */
  classCount: number;
}

export function teacherLoads(
  slots: Record<SlotId, TimetableSlot>,
): Record<string, TeacherLoad> {
  const out: Record<string, TeacherLoad> = {};
  const classesByTeacher: Record<string, Set<string>> = {};

  const ensure = (id: string): TeacherLoad => {
    if (!out[id]) {
      out[id] = { teacherId: id, teachingPeriods: 0, supervisionPeriods: 0, classCount: 0 };
      classesByTeacher[id] = new Set();
    }
    return out[id];
  };

  for (const s of Object.values(slots)) {
    const periods = slotLength(s);
    for (const g of slotGroups(s)) {
      for (const tid of g.teacherIds ?? []) {
        const rec = ensure(tid);
        if (g.kind === 'supervision') rec.supervisionPeriods += periods;
        else rec.teachingPeriods += periods;
        (g.classes ?? s.classes ?? []).forEach((c) => classesByTeacher[tid].add(c));
      }
    }
  }
  for (const id of Object.keys(out)) out[id].classCount = classesByTeacher[id].size;
  return out;
}

/* ==================== display ==================== */

/** What a grid cell says, independent of how it is rendered. */
export function describeGroup(g: SlotGroup): string {
  const label = g.subjectLabel || '—';
  return g.streamLabel ? `${label}` : label;
}

/** Teachers of a group, as codes, including any not yet in the portal. */
export function groupTeacherLabels(
  g: SlotGroup,
  codeOf: (teacherId: string) => string | undefined,
): string[] {
  const out = (g.teacherIds ?? []).map((t) => codeOf(t) ?? '?');
  (g.unresolvedTeachers ?? []).forEach((u) => out.push(u.code ?? u.name ?? '?'));
  return out;
}
